import type { QueryObserverResult } from "@tanstack/react-query"
import { useSetRecoilState, useRecoilState } from "recoil"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import type {
  FindProposalInput,
  Proposal,
  GetProposalQuery,
} from "~/graphql/client"
import { useGetProposalQuery } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"

import { isProposal, proposalRemoteState } from "./proposal-state"

export const useFetchProposalState = (slug: string): Proposal => {
  const { client } = useGraphqlClient()
  const [proposal, setProposal] = useRecoilState(proposalRemoteState)

  const onError = useDispatchError()

  useGetProposalQuery(
    client,
    { findProposalInput: { slug } },
    {
      staleTime: Infinity,
      enabled: !!slug,
      onSuccess: (data) => {
        if (isProposal(data.proposal)) {
          setProposal(data.proposal)
        } else {
          onError(data)
        }
      },
      onError,
    },
  )

  return proposal
}

export type UseProposalFetcherProps = {
  slug?: string
  id?: string
}

export const useProposalFetcher = ({
  slug,
  id,
}: UseProposalFetcherProps): {
  refetch: () =>
    | Promise<QueryObserverResult<GetProposalQuery, unknown>>
    | Promise<void>
  isLoading: boolean
} => {
  const { client } = useGraphqlClient()
  const setProposal = useSetRecoilState(proposalRemoteState)

  const findProposalInput: FindProposalInput = id
    ? { id }
    : {
        slug,
      }
  const onError = useDispatchError()

  const { refetch, isLoading } = useGetProposalQuery(
    client,
    { findProposalInput: findProposalInput },
    {
      enabled: false,
      staleTime: Infinity,
      onSuccess: (data) => {
        if (isProposal(data.proposal)) {
          setProposal(data.proposal)
        } else {
          onError(data)
        }
      },
      onError,
    },
  )

  return { refetch: slug || id ? refetch : async () => undefined, isLoading }
}
